// Inscription en libre-service (D-088) : ce que la page de création (/#/creer/<jeton>) envoie à la
// base, et le suivi des demandes de la page de vente dans la console (lien envoyé, ouvert, espace
// créé, expiré ; question répondue par e-mail). Fonctions pures (testées).
import { isHexColor } from './brand';
import type { TeamDraft } from './console';

/** Jeton d'un lien de création (64 caractères hexadécimaux, 32 au moins). */
export const isSignupToken = (s: string): boolean => /^[0-9a-f]{32,128}$/.test(s);

/** « de Basket Club… », « d’ASC Valdorne » (élision devant une voyelle). */
export const deClub = (name: string): string => (/^[aeiouyhàâäéèêëîïôöûü]/i.test(name.trim()) ? `d’${name.trim()}` : `de ${name.trim()}`);

export interface CreateDraft { name: string; short: string; color: string; ffbb: boolean; teams: TeamDraft[] }
export interface CreatePayload {
  name: string; short_name: string | null; primary_color: string | null; ffbb: boolean;
  teams: { name: string; category: string | null; gender: 'F' | 'M' | 'X'; links: TeamDraft['links'] }[];
}

/** Ce qui manque avant d'envoyer (null : rien). */
export function draftProblem(d: Pick<CreateDraft, 'name' | 'short'>): string | null {
  const n = d.name.trim();
  if (n.length < 2) return 'Le nom du club, s’il vous plaît.';
  if (n.length > 120) return 'Le nom du club : 120 caractères au plus.';
  if (d.short.trim().length > 12) return 'Le sigle : 12 caractères au plus.';
  return null;
}

/** Ce que la base reçoit : les équipes cochées seulement, des textes nettoyés et bornés. */
export function createPayload(d: CreateDraft): CreatePayload {
  return {
    name: d.name.trim().replace(/\s+/g, ' ').slice(0, 120),
    short_name: d.short.trim().slice(0, 12) || null,
    primary_color: isHexColor(d.color) ? d.color.toUpperCase() : null,
    ffbb: d.ffbb,
    teams: d.teams.filter(t => t.on).slice(0, 40).map(t => ({
      name: t.name.trim().replace(/\s+/g, ' ').slice(0, 80) || 'Équipe', category: t.category, gender: t.gender, links: t.links.slice(0, 12),
    })),
  };
}

// --- Suivi dans la console ------------------------------------------------------------------------------
export type MailStatus = 'queued' | 'sending' | 'sent' | 'failed' | 'skipped';
export interface SignupTrack {
  kind: 'signup' | 'question'; status: 'new' | 'contacted' | 'created' | 'declined';
  mail_status: MailStatus | null; token_expires_at: string | null; opened_at: string | null; answer: string | null;
}
export type SignupStage =
  | 'new' | 'queued' | 'sent' | 'opened' | 'created' | 'expired' | 'failed' | 'skipped' | 'declined'   // inscriptions
  | 'waiting' | 'answered' | 'contacted';                                                             // questions

/** Où en est une demande : l'e-mail, le lien, l'espace ; pour une question, la réponse. */
export function signupStage(s: SignupTrack, now: Date = new Date()): SignupStage {
  if (s.status === 'declined') return 'declined';
  const mail = s.mail_status === 'sending' ? 'queued' : s.mail_status;
  if (s.kind === 'question') {
    if (!s.answer) return s.status === 'contacted' ? 'contacted' : 'waiting';
    return mail === 'sent' ? 'answered' : mail ?? 'answered';
  }
  if (s.status === 'created') return 'created';
  if (!mail) return 'new';                                                   // demande d'avant D-088
  if (s.token_expires_at && Date.parse(s.token_expires_at) <= now.getTime()) return 'expired';
  if (mail === 'sent') return s.opened_at ? 'opened' : 'sent';
  return mail;
}

const SIGNUP_LABEL: Record<SignupStage, string> = {
  new: 'Nouvelle', queued: 'E-mail en cours d’envoi', sent: 'Lien envoyé', opened: 'Lien ouvert', created: 'Espace créé', expired: 'Lien expiré',
  failed: 'E-mail non parti', skipped: 'E-mail non envoyé', declined: 'Refusée', waiting: 'Nouvelle', answered: 'Répondue', contacted: 'Contactée',
};
const QUESTION_LABEL: Record<SignupStage, string> = {
  ...SIGNUP_LABEL, waiting: 'Réponse automatique à venir', queued: 'Réponse en cours d’envoi', answered: 'Répondue par e-mail',
  contacted: 'Répondue', failed: 'Réponse non partie', skipped: 'Réponse non envoyée', declined: 'Classée',
};
export const stageLabel = (kind: SignupTrack['kind'], st: SignupStage): string => (kind === 'question' ? QUESTION_LABEL : SIGNUP_LABEL)[st];

/** L'éditeur reprend la main (secours) : demande d'avant D-088, ou e-mail qui n'est pas parti. */
export const needsHand = (st: SignupStage): boolean => st === 'new' || st === 'failed' || st === 'skipped';
/** Encore en cours (sinon : rangée dans « traitées »). */
export const isOpenStage = (st: SignupStage): boolean => !['created', 'declined', 'expired', 'answered', 'contacted'].includes(st);

/** Pourquoi un e-mail n'est pas parti (mail_error). */
export function skipReason(error: string | null): string {
  if (error === 'demo') return 'adresse d’exemple';
  if (error === 'optout') return 'adresse désinscrite des e-mails d’Arceau';
  if (error === 'closed') return 'demande déjà close';
  return error ?? 'raison inconnue';
}
